<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="5" />
      <vs-col
        vs-w="6"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4">
          <div class="flex items-center w-full">
            <select-with-multiple
              :is-busy="isBusy"
              v-model="filters.owners"
              style="min-width: 260px;"
              :options="ownerOptions"
              placeholder="Select Owners"
              :activate-search="true"
            />
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchBargeManagements()"
        ref="tableTop"
      >
        <template #start>
          <div
            class="flex items-center justify-center"
          >
            <vs-button
              class="button shadow-primary font-semibold"
              color="primary"
              v-show="isUserCanAccessMenu(permission.name, 'add')"
              @click="startAdd"
            >
              Add New Barge
            </vs-button>
            <vs-button
              class="button shadow-secondary font-semibold ml-5"
              color="secondary"
              @click="isDownload = true"
            >
              Export
            </vs-button>
          </div>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :loading="isBusy"
        :table-header="bargeManagementHeaders"
        :table-data="bargeManagementData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :frozen-columns="isActionColumnVisible ? 4 : 3"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row">
            <template v-if="row.isEditing">
              <div
                :class="`mx-1 self-center flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing ? null : saveEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`mx-1 self-center flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing ? null : cancelEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="mx-1 self-center flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img :src="require('@/assets/shipo-icon-edit.svg')">
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="mx-1 self-center flex items-center justify-center icon-transparent-bg"
                @click="startDelete(row)"
              >
                <img :src="require('@/assets/shipo-icon-delete.svg')">
              </div>
            </template>
          </div>
        </template>
        <template #owner_id="{ row }">
          <template v-if="row.isEditing">
            <div
              class="relative"
              style="min-width: 18rem;"
            >
              <div>
                <select-with-search
                  :disabled="isProcessing"
                  v-model="row.editForm.owner_id.value"
                  :class="[!row.editForm.owner_id.isValid ? 'is-not-valid' : ''].join(' ')"
                  class="w-56"
                  :options="ownerOptions"
                  placeholder="Select owner"
                />
              </div>
              <span
                v-if="!row.editForm.owner_id.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.owner_name || '-' }}
          </template>
        </template>
        <template #name="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.name.value"
                  :class="{ 'is-not-valid': !row.editForm.name.isValid }"
                  placeholder="Enter barge name"
                />
              </div>
              <span
                v-if="!row.editForm.name.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.name || '-' }}
          </template>
        </template>
        <template #active="{ row }">
          <template v-if="row.isEditing">
            <vs-switch
              :disabled="isProcessing"
              v-model="row.editForm.active.value"
              color="secondary"
            />
          </template>
          <template v-else>
            <div
              :class="{
                'bg-green-200 text-green-700 border-green-400': row.active,
                'bg-red-200 text-red-700 border-red-400': !row.active
              }"
              :disabled="!isUserCanAccessMenu(permission.name, 'is_active')"
              class="button-l-plan w-20 items-center"
            >
              <span>{{ stateOptions.find((state) => state.value === row.active.toString()).text }}</span>
            </div>
          </template>
        </template>
        <template #barge_size="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.barge_size.value"
                  :class="{ 'is-not-valid': !row.editForm.barge_size.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter barge size"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.barge_size.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.barge_size ? handleValidateAndFormatNumber(row.barge_size) : 0 }}
            </div>
          </template>
        </template>
        <template #grt="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.grt.value"
                  :class="{ 'is-not-valid': !row.editForm.grt.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter barge GRT"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.grt.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.grt ? handleValidateAndFormatNumber(row.grt) : 0 }}
            </div>
          </template>
        </template>
        <template #build_year="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <date-picker
                  v-model="row.editForm.build_year.value"
                  type="year"
                  value-type="format"
                  format="YYYY"
                  :class="{ 'is-not-valid': !row.editForm.build_year.isValid }"
                  placeholder="Select build year"
                  :disabled="isProcessing"
                  :max="new Date()"
                />
              </div>
              <span
                v-if="!row.editForm.build_year.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.build_year || '-' }}
          </template>
        </template>
        <template #class="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.class.value"
                  :class="{ 'is-not-valid': !row.editForm.class.isValid }"
                  placeholder="Enter barge class"
                />
              </div>
              <span
                v-if="!row.editForm.class.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.class || '-' }}
          </template>
        </template>
        <template #length="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.length.value"
                  :class="{ 'is-not-valid': !row.editForm.length.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter barge length"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.length.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.length ? handleValidateAndFormatNumber(row.length) : 0 }}
            </div>
          </template>
        </template>
        <template #beam="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.beam.value"
                  :class="{ 'is-not-valid': !row.editForm.beam.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter barge beam"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.beam.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.beam ? handleValidateAndFormatNumber(row.beam) : 0 }}
            </div>
          </template>
        </template>
        <template #depth="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.depth.value"
                  :class="{ 'is-not-valid': !row.editForm.depth.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter barge depth"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.depth.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.depth ? handleValidateAndFormatNumber(row.depth) : 0 }}
            </div>
          </template>
        </template>
        <template #load_grt="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.load_grt.value"
                  :class="{ 'is-not-valid': !row.editForm.load_grt.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter barge GRT load"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.load_grt.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.load_grt ? handleValidateAndFormatNumber(row.load_grt) : 0 }}
            </div>
          </template>
        </template>
        <template #min_load="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.min_load.value"
                  :class="{ 'is-not-valid': !row.editForm.min_load.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter minimum load"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.min_load.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.min_load ? handleValidateAndFormatNumber(row.min_load) : 0 }}
            </div>
          </template>
        </template>
        <template #max_load="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.max_load.value"
                  :class="{ 'is-not-valid': !row.editForm.max_load.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter maximum load"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.max_load.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.max_load ? handleValidateAndFormatNumber(row.max_load) : 0 }}
            </div>
          </template>
        </template>
        <template #plan_load="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.plan_load.value"
                  :class="{ 'is-not-valid': !row.editForm.plan_load.isValid }"
                  class="vs-input--input w-40"
                  placeholder="Enter plan load"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.plan_load.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            <div class="text-right">
              {{ row.plan_load ? handleValidateAndFormatNumber(row.plan_load) : 0 }}
            </div>
          </template>
        </template>
        <template #image_file="{ row }">
          <template v-if="row.isEditing">
            <div class="flex flex-row items-center">
              <input
                type="file"
                id="fileInput"
                ref="fileInput"
                class="hidden"
                accept="image/*"
                :disabled="isProcessing"
                @change="handleFileUpload($event, row)"
              >
              <vs-button
                class="bg-secondary-accent text-secondary py-1 w-64 h-8"
                color="primary"
                type="button"
                :disabled="isProcessing"
                @click="$refs.fileInput.click()"
              >
                <div class="flex flex-row items-center justify-start pr-2 w-full">
                  <img
                    :src="require('@/assets/shipo-icon-edit-upload.svg')"
                    class="flex-shrink-0 mr-2 h-6"
                    alt="upload"
                  >
                  <div class="truncate">
                    {{ row.editForm.image_file.value ? row.editForm.image_file.file.name : 'Select File' }}
                  </div>
                </div>
              </vs-button>
            </div>
          </template>
          <template v-else>
            <vs-button
              class="bg-secondary text-white py-1 w-64 h-8"
              color="primary"
              type="button"
              :disabled="!(row.image_file || (row.image && row.image.downloadUrl))"
              @click="openImageModal(row)"
            >
              <div class="flex flex-row items-center justify-start pr-2 w-full">
                <img
                  :src="require('@/assets/shipo-icon-see.svg')"
                  class="flex-shrink-0 mr-2 h-6"
                >
                <div class="truncate">
                  {{ row.image ? row.image.name : 'No Photo' }}
                </div>
              </div>
            </vs-button>
          </template>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchBargeManagements()"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete Barge"
      left-button="Cancel"
      right-button="Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null }"
      @leftButton="popUp = { isShown: false, tempId: null }"
      @rightButton="deleteBargeManagement"
    >
      <p class="text-center mt-2">
        Are you sure you want to delete
      </p>
      <p class="text-center mt-2">
        "{{ popUp.tempId ? bargeManagementData.find((item) => item.id === popUp.tempId).name : '' }}"?
      </p>
      <p class="text-center mt-2">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUpSuccess"
      @close="resetTableFilter"
      @ok="resetTableFilter"
    >
      You have successfully delete barge
    </pop-up>
    <pop-up
      :active.sync="popUp.isRestoreShown"
      is-warning
      title="Restore Barge"
      left-button="Cancel"
      right-button="Restore"
      :is-busy="isProcessing"
      @close="closeRestoreModal"
      @leftButton="closeRestoreModal"
      @rightButton="restoreBargeData"
    >
      <p class="text-center mt-1">
        {{ restoreData.formData.name }} already exists but has been deleted.
      </p>
      <p class="text-center">
        Would you like to restore it?
      </p>
    </pop-up>
    <base-custom-modal
      :is-shown="isImageModalShown"
      @update:isShown="isImageModalShown=$event"
    >
      <template #title>
        <span class="font-semibold">Barge Photo</span>
      </template>
      <template #body>
        <img
          v-if="selectedImageUrl"
          :src="selectedImageUrl"
          alt="Barge Photo"
          class="w-full h-auto object-contain rounded-3xl border-2"
        >
      </template>
    </base-custom-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import api from "@/api";
import BaseCustomModal from "@/components/baseCustomModal";
import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import PopUp from '@/components/popUp'
import SelectWithMultiple from "@/components/selectWithMultiple";
import SelectWithSearch from "@/components/selectWithSearch/index.vue";
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { MAX_FILE_UPLOAD_SIZE_BYTES, PAGE_TITLE } from '@/fixtures/Constants'

import downloadUtils from '../../../../utils/downloadUtils';

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DataTable,
    PopUp,
    TableBottom,
    TableTop,
    SelectWithMultiple,
    SelectWithSearch,
    BaseCustomModal,
    DatePicker
  },
  data() {
    return {
      popUp: {
        isShown: false,
        tempId: null,
        isRestoreShown: false
      },
      popUpSuccess: false,
      bargeManagementHeaders: [
        { key: 'no', label: 'No', sortable: false, width: '20px' },
        { key: 'owner_id', label: 'Owner Name', sortable: true },
        { key: 'name', label: 'Barge Name', sortable: true },
        { key: 'active', label: 'Status', sortable: true },
        { key: 'barge_size', label: 'Barge Size (ft)', sortable: true },
        { key: 'grt', label: 'GRT Barge', sortable: true },
        { key: 'build_year', label: 'Barge Build', sortable: true },
        { key: 'class', label: 'Class Barge', sortable: true },
        { key: 'length', label: 'Length', sortable: true },
        { key: 'beam', label: 'Beam', sortable: true },
        { key: 'depth', label: 'Depth', sortable: true },
        { key: 'load_grt', label: 'Load GRT', sortable: true },
        { key: 'min_load', label: 'Min. Load', sortable: true },
        { key: 'max_load', label: 'Max. Load', sortable: true },
        { key: 'plan_load', label: 'Plan Load', sortable: true },
        { key: 'image_file', label: 'Barge Photo', sortable: false }
      ],
      bargeManagementData: [],
      restoreData: {
        id: null,
        name: null,
        formData: {}
      },
      filters: {
        sort_by: '',
        sort_dir: '',
        owners: []
      },
      stateOptions: [
        { value: null, text: 'All' },
        { value: 'true', text: 'Active' },
        { value: 'false', text: 'Inactive' }
      ],
      ownerOptions: [],
      options: {
        owner: []
      },
      isAdding: false,
      isImageModalShown: false,
      selectedImageUrl: null,
      isDownload: false,
      isBusy: false,
      isProcessing: false
    }
  },
  computed: {
    ...mapGetters({
      bargeManagements: 'BargeManagement/bargeManagements',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      bargeManagementsFiles: 'BargeManagement/bargeManagementsFiles',
      companies: 'Companies/companies'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  mounted() {
    this.fetchOwners()
    this.fetchBargeManagements()
  },
  watch: {
    bargeManagements: {
      handler(val) {
        if (val) {
          this.bargeManagementData = val.map(item => ({
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.bargeManagementHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.bargeManagementHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.bargeManagementHeaders.splice(index, 1);
        }
      }
    },
    filters: {
      deep: true,
      handler(val) {
        this.fetchBargeManagements()
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadBargeManagements();
        }
      }
    },
    bargeManagementsFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Barge_managements.xlsx`)
          // Reset the companiesFiles state
          this.$store.dispatch('BargeManagement/resetBargeManagementsFiles');
        }
      }
    },
    companies: {
      handler(val) {
        if (val) {
          this.ownerOptions = val.map(item => ({
            value: item.name,
            text: item.name,
            id: item.id
          }))
        }
      }
    }
  },
  methods: {
    async fetchOwners() {
      try {
        this.isBusy = true;
        await this.$store.dispatch('Companies/fetchAllCompanies', {
          is_master_data: true,
          type: 'TUG_OWNER',
          status: true,
          sort_dir: 'asc',
          sort_by: 'name',
          page: null,
          limit: null
        })
        this.isBusy = false;
      } catch (e) {
        this.isBusy = false;
        this.showFailedNotification(
          this,
          'Failed to fetch Owner, please try again',
          'Error'
        )
      }
    },
    async fetchBargeManagements() {
      try {
        this.isBusy = true
        this.isAdding = false;
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key])
          .reduce((item, key) => {
            // Special handling for owners array to extract only the values
            if (key === 'owners') {
              item.owners = this.filters.owners.map(owner => owner.text).join(",") || null
            } else {
              item[key] = this.filters[key]
            }
            return item
          }, {})
        await this.$store.dispatch(
          'BargeManagement/fetchBargeManagements',
          apiFilters
        )
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(
          this,
          'Failed to fetch Barge Management list, please try again',
          'Error'
        )
      }
    },
    async putBargeManagement(id, value) {
      try {
        await this.$store.dispatch('BargeManagement/putBargeManagement', {
          id: id,
          form: {
            active: value
          }
        })
        this.showSuccessNotification(this, `You have change to ${value ? 'Active' : 'Inactive'}`)
      } catch (e) {
        this.showFailedNotification(this, `Failed change to ${value ? 'Active' : 'Inactive'}, please try again`)
      }
    },
    async deleteBargeManagement() {
      try {
        this.isProcessing = true;
        await this.$store.dispatch('BargeManagement/deleteBargeManagement', this.popUp.tempId)
        this.popUp = { isShown: false, tempId: null }
        this.isProcessing = false
        this.showSuccessNotification(this, 'Barge successfully deleted.')
      } catch (e) {
        this.isProcessing = false
        this.showFailedNotification(
          this,
          'Failed to delete Barge Management , please try again',
          'Error'
        )
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchBargeManagements()
      this.popUpSuccess = false
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir

      this.fetchBargeManagements()
    },
    isNumber(value) {
      return typeof value === 'number' || (!isNaN(value) && value !== '' && value !== null);
    },
    startAdd() {
      // Cancel any existing edit
      if (this.isAdding || this.bargeManagementData.some(row => row.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      // Create new row
      const newRow = {
        id: `new-${Date.now()}`, // Temporary ID for new row
        isEditing: true,
        active: true,
        editForm: {
          name: { value: '', isValid: true },
          owner_id: { value: '', isValid: true },
          barge_size: { value: '', isValid: true },
          grt: { value: '', isValid: true },
          build_year: { value: '', isValid: true },
          class: { value: '', isValid: true },
          length: { value: '', isValid: true },
          beam: { value: '', isValid: true },
          depth: { value: '', isValid: true },
          load_grt: { value: '', isValid: true },
          min_load: { value: '', isValid: true },
          max_load: { value: '', isValid: true },
          plan_load: { value: '', isValid: true },
          image_file: { value: '' },
          active: { value: true }
        }
      }

      this.isAdding = true
      this.bargeManagementData.unshift(newRow)
    },
    startEdit(row) {
      // Cancel any existing edit or add
      if (this.isAdding || this.bargeManagementData.some(r => r.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      const index = this.bargeManagementData.findIndex(item => item.id === row.id)
      if (index !== -1) {
        this.$set(this.bargeManagementData[index], 'isEditing', true)
        this.$set(this.bargeManagementData[index], 'editForm', {
          name: { value: row.name, isValid: true },
          barge_size: { value: row.barge_size, isValid: true },
          grt: { value: row.grt, isValid: true },
          class: { value: row.class, isValid: true },
          length: { value: row.length, isValid: true },
          beam: { value: row.beam, isValid: true },
          depth: { value: row.depth, isValid: true },
          load_grt: { value: row.load_grt, isValid: true },
          min_load: { value: row.min_load, isValid: true },
          max_load: { value: row.max_load, isValid: true },
          plan_load: { value: row.plan_load, isValid: true },
          owner_id: {
            value: row.owner_name
              ? this.ownerOptions.find(option => option.text === row.owner_name)?.value
              : '',
            isValid: true
          },
          build_year: {
            value: row.build_year ? `${row.build_year}` : '',
            isValid: true
          },
          image_file: { value: null },
          active: { value: row.active }
        })
      }
    },
    startDelete(row) {
      // Cancel any existing edit or add
      if (this.isAdding || this.bargeManagementData.some(r => r.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      this.popUp = {
        isShown: true,
        tempId: row.id
      }
    },
    cancelAllEdits() {
      this.bargeManagementData.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false),
          this.$set(row, 'editForm', null)
        }
      })
      this.isAdding = false
    },
    cancelEdit(row) {
      if (this.isAdding) {
        // Remove the new row if we're canceling an add
        const index = this.bargeManagementData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.bargeManagementData.splice(index, 1)
        }
      } else {
        // Just cancel the edit
        const index = this.bargeManagementData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.$set(this.bargeManagementData[index], 'isEditing', false)
          this.$set(this.bargeManagementData[index], 'editForm', null)
        }
      }
      this.isAdding = false
    },
    async saveEdit(row) {
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(
          this,
          'Some fields is empty, please fill it first',
          'Failed'
        )
        return
      }

      try {
        const formData = {
          ...Object.keys(row.editForm).reduce((item, key) => {
            item[key] = row.editForm[key].value
            return item
          }, {}),
          owner_id: this.ownerOptions.find(item => item.value === row.editForm.owner_id.value).id,
          owner_name: this.ownerOptions.find(item => item.value === row.editForm.owner_id.value).text,
          image_file: row.editForm.image_file.value || null
        }
        this.isProcessing = true
        const isRestoreData = await this.checkRestoreData(formData)
        if (isRestoreData) {
          this.isProcessing = false
          return
        }

        if (this.isAdding) {
          // remove active when adding
          delete formData.active
          // Handle add
          await this.$store.dispatch('BargeManagement/postBargeManagement', formData)
          this.showSuccessNotification(this, 'Barge successfully added.')
        } else {
          // Handle edit
          await this.$store.dispatch('BargeManagement/putBargeManagement', {
            id: row.id,
            form: formData
          })
          this.showSuccessNotification(this, 'Barge successfully updated')
        }

        // Reset state
        this.cancelAllEdits()
        this.isProcessing = false
        await this.fetchBargeManagements()
      } catch ({ error }) {
        this.isProcessing = false
        this.showFailedNotification(
          this,
          error?.message || 'Failed to save changes, please try again',
          'Error'
        )
      }
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },
    handleFileUpload(event, row) {
      const file = event.target.files[0]
      if (file) {
        if (file.size > MAX_FILE_UPLOAD_SIZE_BYTES) {
          this.showFailedNotification(this, 'File may not be greater than 5MB')
          event.target.value = '' // Reset value to trigger onchange listener
          return
        }

        const reader = new FileReader()
        reader.onload = (e) => {
          const base64 = e.target.result
          row.editForm.image_file = {
            value: base64,
            file: file,
            isValid: true
          }
        }
        reader.readAsDataURL(file)
      }
    },
    openImageModal(row) {
      if (row.image && row.image.downloadUrl) {
        this.selectedImageUrl = row.image.downloadUrl
        this.isImageModalShown = true
      }
    },
    closeRestoreModal() {
      this.popUp.isRestoreShown = false
      this.restoreData = {
        id: null,
        name: null,
        formData: {}
      }
    },
    async restoreBargeData() {
      try {
        await this.$store.dispatch('BargeManagement/putBargeManagement', {
          id: this.restoreData.id,
          form: {
            ...this.restoreData.formData,
            is_restore: true
          }
        })
        this.showSuccessNotification(this, 'Barge successfully updated')

        // Reset state
        this.cancelAllEdits()
        this.closeRestoreModal()
        this.fetchBargeManagements()
      } catch (e) {
        this.showFailedNotification(
          this,
          error?.message || 'Failed to save changes, please try again',
          'Error'
        )
      }
    },
    async checkRestoreData(formData) {
      try {
        const {data: { id, name } } = await api.getBargeManagements({
          name: formData.name,
          is_get_deleted: true
        })
        if (id && name) {
          this.popUp.isRestoreShown = true
          this.restoreData.id = id
          this.restoreData.name = name
          this.restoreData.formData = formData

          return true
        }
        return false
      } catch (e) {
        this.showFailedNotification(
          this,
          error?.message || 'Failed to restore data, please try again',
          'Error'
        )
      }
    },
    resetFilters() {
      this.filters = {
        sort_by: '',
        sort_by_name: '',
        owners: []
      };
      this.$refs.tableTop.$emit('reset-search')

      this.fetchBargeManagements()
    },
    getInvalidFields(form) {
      const invalidFields = Object.keys(form).reduce((acc, key) => {
        if (form[key].hasOwnProperty('isValid') && !form[key].isValid) {
          const header = this.bargeManagementHeaders.find((header) => header.key === key)
          acc.push(header.label.split(' ').slice(0, 2).join(' '))
        }
        return acc
      }, [])

      if (invalidFields.length === 1) return `${invalidFields[0]}`
      if (invalidFields.length === 2) return `${invalidFields[0]} and ${invalidFields[1]}`
      return `${invalidFields.slice(0, -1).join(', ')}, and ${invalidFields[invalidFields.length - 1]}`
    },
    async downloadBargeManagements() {
      try {
        this.isBusy = true
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key])
          .reduce((item, key) => {
            // Special handling for owners array to extract only the values
            if (key === 'owners') {
              item.owners = this.filters.owners.map(owner => owner.text).join(",") || null
            } else {
              item[key] = this.filters[key]
            }
            return item
          }, {})
        await this.$store.dispatch('BargeManagement/downloadBargeManagements', {
          ...apiFilters,
          limit: undefined,
          page: undefined
        }, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isDownload = false;
        this.isBusy = false;
      } catch (error) {
        this.isBusy = false;
        this.isDownload = false;
        this.showFailedNotification(this, 'Failed to download barge managements, please try again');
      }
    }
  },
  beforeCreate() {
    this.$store.dispatch('Table/resetConfig')
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.bg-contract {
  @apply py-1;
  @apply px-3;
  @apply font-semibold;
  @apply rounded-lg;

  &.new {
    @apply text-primary;
    background-color: #F1F1FD;
  }

  &.updated {
    @apply text-danger;
    @apply bg-danger-accent;
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}
</style>
